/**
 * Service for getting captured image URLs
 *
 * IMPORTANT: Captured images are stored PERMANENTLY in S3
 * - No lifecycle policies (never auto-deleted)
 * - Public read access for event history/audit trail
 * - Direct URLs that never expire
 * - Stored at: s3://shcaptures100843-staging/public/captures/temp/{userId}/{timestamp}.jpg
 *
 * Note: "temp" in path is legacy naming - files are permanent, not temporary
 *
 * SECURITY DESIGN HISTORY:
 * ---------------------------
 * PREVIOUS DESIGN (Before 2026-05-30):
 * - S3 bucket had BlockPublicAcls: true, IgnorePublicAcls: true, BlockPublicPolicy: true, RestrictPublicBuckets: true
 * - All captured images required pre-signed URLs generated via Storage.get()
 * - Signed URLs expired after configurable period (default 15 minutes)
 * - This caused issues in Event Aggregator: displaying 100 events required 100 Storage.get() API calls
 * - Pre-signed URLs would expire, breaking image links in historical event views
 *
 * CURRENT DESIGN (After 2026-05-30):
 * - Enabled public read access for public/captures/* path only
 * - Direct URLs never expire, no API calls needed
 * - Event Aggregator can display all captured images instantly
 * - Trade-off: Anyone with URL can view captured images (acceptable for audit/compliance use case)
 *
 * ROLLBACK INSTRUCTIONS (if needed):
 * To revert to private access with signed URLs:
 * 1. Re-enable S3 public access blocks:
 *    aws s3api put-public-access-block --bucket shcaptures100843-staging --region eu-west-2 \
 *      --public-access-block-configuration BlockPublicAcls=true,IgnorePublicAcls=true,BlockPublicPolicy=true,RestrictPublicBuckets=true
 * 2. Remove bucket policy allowing public reads
 * 3. Replace this service with signed URL generation (use Storage.get() from aws-amplify)
 * 4. Update EventTable.jsx to handle async signed URL generation
 * 5. Consider server-side URL generation for better performance
 */

import { getTenantConfig, getTenantId } from '../../config/tenantConfig';

const S3_REGION = 'eu-west-2';

/**
 * DEPRECATED: Frontend should NOT construct S3 URLs - backend must return full URLs
 *
 * This function should never be called. If you see errors from this function,
 * it means the backend is returning S3 keys instead of full URLs.
 *
 * Fix: Update backend API to return full URLs in capturedImageUrl field.
 *
 * @throws {Error} Always throws - frontend must be tenant-agnostic
 */
function getCapturesBucket() {
  throw new Error(
    'BACKEND ERROR: capturedImageUrl contains S3 key instead of full URL. ' +
    'Backend APIs must return full URLs (https://bucket.s3.region.amazonaws.com/key), not S3 keys. ' +
    'Frontend cannot construct URLs because it does not know the tenant ID (tenant-agnostic design).'
  );
}

/**
 * Get direct public URL for a captured image
 * Returns a PERMANENT URL that never expires (unlike signed URLs)
 *
 * @param {string} s3KeyOrPath - S3 key or path from database
 *   - Database format: "/public/captures/2026-05-29/userId/success/123_456.jpg"
 *   - S3 key format: "public/captures/temp/userId/123.jpg"
 * @returns {string} Public S3 URL (permanent, no expiration, no auth needed)
 */
export function getPublicCaptureUrl(s3KeyOrPath) {
  if (!s3KeyOrPath) return null;

  // Remove leading slash if present (database stores "/public/..." format)
  const s3Key = s3KeyOrPath.startsWith('/') ? s3KeyOrPath.substring(1) : s3KeyOrPath;

  // S3 key path components need to be individually encoded
  // Split by '/', encode each part, then rejoin
  // This handles special characters like '+', '@', spaces in userIds
  const encodedKey = s3Key
    .split('/')
    .map(part => encodeURIComponent(part))
    .join('/');

  // Get tenant-aware bucket name
  const bucket = getCapturesBucket();

  // S3 public URL format: https://bucket.s3.region.amazonaws.com/key
  return `https://${bucket}.s3.${S3_REGION}.amazonaws.com/${encodedKey}`;
}

/**
 * Check if a captured image URL is likely to be accessible
 * (Heuristic: if it's an S3 key, we can convert it to public URL)
 *
 * @param {string} urlOrKey - Either an S3 key or already a URL
 * @returns {boolean} True if we can get a URL from this
 */
export function isCaptureImageAvailable(urlOrKey) {
  if (!urlOrKey) return false;

  // Already a URL
  if (urlOrKey.startsWith('http://') || urlOrKey.startsWith('https://')) {
    return true;
  }

  // S3 key pattern
  if (urlOrKey.includes('captures/') || urlOrKey.startsWith('public/')) {
    return true;
  }

  return false;
}

/**
 * Get captured image URL (handles both temp and permanent locations)
 *
 * ARCHITECTURE:
 * - Images start in temp location: public/captures/temp/...
 * - Matched/associated images are asynchronously copied to permanent: public/captures/YYYY-MM-DD/userId/success/...
 * - Async worker updates event metadata.capturedImageUrl with permanent path
 * - Temp images are deleted after successful copy
 * - S3 lifecycle policy deletes orphaned temp images after 1 day
 *
 * This function just converts S3 key to public URL.
 * The event metadata always has the correct path (temp or permanent).
 *
 * @param {string} capturedImageUrl - From event.metadata.capturedImageUrl (auto-updated by async worker)
 * @returns {string|null} Displayable image URL
 */
export function getCapturedImageDisplayUrl(capturedImageUrl) {
  if (!capturedImageUrl) {
    return null;
  }

  // If already a URL (pre-signed), use as-is
  if (capturedImageUrl.startsWith('http://') || capturedImageUrl.startsWith('https://')) {
    return capturedImageUrl;
  }

  // Otherwise treat as S3 key and construct public URL
  // Works for both temp and permanent paths since event metadata is auto-updated
  return getPublicCaptureUrl(capturedImageUrl);
}
